import React from 'react';
import { 
  GraduationCap, 
  Award, 
  MapPin, 
  Clock,
  CheckCircle,
  Star,
  Users
} from 'lucide-react';
import { Banner } from './Banner';

export const About = () => {
  const credentials = [
    "Doctor of Physical Therapy (DPT)",
    "Licensed Physical Therapist",
    "Sports Injury Specialist",
    "Orthopedic Rehabilitation Expert",
    "20+ Years Clinical Experience"
  ];

  const achievements = [
    { number: "1000+", label: "Patients Treated" },
    { number: "20+", label: "Years Experience" }
  ];

  return (
    <>
      {/* Shared Banner with title */}
      <Banner title="Expert Care You Can Trust" reduced={true} />

      <section id="about" className="pt-8 pb-20" style={{ backgroundColor: 'rgba(26, 21, 18, 0.8)' }}>
        <div className="container">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            {/* Removed duplicate pill for continuity */}
            
            <p className="body-large mb-6">
              Howdy! I’m Douglas, and I love helping people discover ways to help their bodies heal, recover and perform better. I have been working with a WIDE variety of clients over the past 24+ years, and have found there really is no “one size fits all” for recovery (although I am a firm believer in the old adage of “you can’t go wrong getting strong” for many conditions).
            </p>

            <p className="body-large mb-3">Some people might care or are curious about my credentials:</p>
            <ul className="space-y-2 mb-8">
              <li className="flex items-start"><CheckCircle style={{ color: 'var(--brand-primary)' }} className="mr-3 mt-0.5 flex-shrink-0" size={18} /> <span>Licensed Physical Therapist in the state of Arizona</span></li>
              <li className="flex items-start"><CheckCircle style={{ color: 'var(--brand-primary)' }} className="mr-3 mt-0.5 flex-shrink-0" size={18} /> <span>Doctor of Physical Therapy (DPT)</span></li>
              <li className="flex items-start"><CheckCircle style={{ color: 'var(--brand-primary)' }} className="mr-3 mt-0.5 flex-shrink-0" size={18} /> <span>Certified Integrative Dry Needling Expert (CIDN) with over 13 years of experience</span></li>
              <li className="flex items-start"><CheckCircle style={{ color: 'var(--brand-primary)' }} className="mr-3 mt-0.5 flex-shrink-0" size={18} /> <span>24 Years of Experience in Outpatient Orthopedic Physical Therapy</span></li>
              <li className="flex items-start"><CheckCircle style={{ color: 'var(--brand-primary)' }} className="mr-3 mt-0.5 flex-shrink-0" size={18} /> <span>Considerable experience with Vertigo including post concussive chronic central instabilities</span></li>
            </ul>

            <p className="body-large mb-6">
              Unfortunately, chronic painful conditions can limit what you do and how you move, leading to living a more sedentary lifestyle. With my experience, I can absolutely assist you in your recovery with coaching, education, encouragement and manual therapies to maximize your healing potential.
            </p>

            <p className="body-large mb-8">
              At my current location inside of <a href="/favorite-places#alexander-medical-center" className="underline hover:no-underline" style={{ color: 'var(--brand-primary)' }}>Alexander Medical Center</a>, I also have access to multiple providers who are also specialists and are passionate about helping their clients maximize their health and wellness. If you are currently a member of the Alexander Medical Clinic, you also receive a discounted rate for Physical Therapy sessions!
            </p>

            <div className="flex items-center space-x-6 p-6 bg-black/50 rounded-xl border" style={{ borderColor: 'var(--border-brick-subtle)' }}>
              <MapPin style={{ color: 'var(--brand-primary)' }} size={24} />
              <div>
                <div className="font-semibold" style={{ color: 'var(--brand-primary)' }}>In Collaboration With</div>
                <div className="body-medium"><a href="/favorite-places#alexander-medical-center" className="underline hover:no-underline" style={{ color: 'var(--brand-primary)' }}>Alexander Medical Center</a></div>
                <div className="text-sm text-gray-400">Gilbert, Arizona</div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_physio-blog-admin/artifacts/o65crtxj_headshot%20.jpg"
                alt="Douglas Mitchell, PT, DPT - Licensed Physical Therapist in Gilbert Arizona specializing in sports injury recovery and orthopedic rehabilitation"
                className="w-full h-96 object-contain rounded-2xl"
                style={{ backgroundColor: '#1a1a1a' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-2xl pointer-events-none"></div>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2">
                <span className="inline-block px-4 py-2 text-xs sm:text-sm font-semibold uppercase tracking-wide rounded-full" style={{ backgroundColor: 'rgba(232, 184, 109, 0.25)', color: 'var(--brand-primary)' }}>
                  Douglas Mitchell, PT, DPT
                </span>
              </div>
            </div>

            {/* Stats Grid - Enhanced */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-black/50 p-6 rounded-xl border text-center" style={{ borderColor: 'var(--border-brick-subtle)' }}>
                <div className="flex items-center justify-center w-12 h-12 rounded-full mb-3 mx-auto" style={{ backgroundColor: 'rgba(232, 184, 109, 0.15)' }}>
                  <Award style={{ color: 'var(--brand-primary)' }} size={20} />
                </div>
                <div className="text-2xl font-bold mb-2" style={{ color: 'var(--brand-primary)' }}>DPT</div>
                <div className="text-sm text-gray-400 uppercase tracking-wide">Licensed</div>
              </div>
              
              <div className="bg-black/50 p-6 rounded-xl border text-center" style={{ borderColor: 'var(--border-brick-subtle)' }}>
                <div className="flex items-center justify-center w-12 h-12 rounded-full mb-3 mx-auto" style={{ backgroundColor: 'rgba(232, 184, 109, 0.15)' }}>
                  <Users style={{ color: 'var(--brand-primary)' }} size={20} />
                </div>
                <div className="text-2xl font-bold mb-2" style={{ color: 'var(--brand-primary)' }}>1000+</div>
                <div className="text-sm text-gray-400 uppercase tracking-wide">Patients</div>
              </div>
              
              <div className="bg-black/50 p-6 rounded-xl border text-center" style={{ borderColor: 'var(--border-brick-subtle)' }}>
                <div className="flex items-center justify-center w-12 h-12 rounded-full mb-3 mx-auto" style={{ backgroundColor: 'rgba(232, 184, 109, 0.15)' }}>
                  <Clock style={{ color: 'var(--brand-primary)' }} size={20} />
                </div>
                <div className="text-2xl font-bold mb-2" style={{ color: 'var(--brand-primary)' }}>20+</div>
                <div className="text-sm text-gray-400 uppercase tracking-wide">Years</div>
              </div>
            </div>
          </div>
        </div>

        {/* Philosophy Section */}
        <div className="mt-20 text-center">
          <h3 className="heading-2 mb-8">Treatment Philosophy</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(232, 184, 109, 0.15)' }}>
                <GraduationCap style={{ color: 'var(--brand-primary)' }} size={24} />
              </div>
              <h4 className="heading-3 mb-4" style={{ color: 'var(--brand-primary)' }}>Evidence-Based</h4>
              <p className="body-medium">
                Using the latest research and proven techniques to maximize your body's healing potential.
              </p>
            </div>
            
            <div className="p-6">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(232, 184, 109, 0.15)' }}>
                <Award style={{ color: 'var(--brand-primary)' }} size={24} />
              </div>
              <h4 className="heading-3 mb-4" style={{ color: 'var(--brand-primary)' }}>Personalized Care</h4>
              <p className="body-medium">
                Every treatment plan is tailored to your specific condition, goals, and healing journey.
              </p>
            </div>
            
            <div className="p-6">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(232, 184, 109, 0.15)' }}>
                <Clock style={{ color: 'var(--brand-primary)' }} size={24} />
              </div>
              <h4 className="heading-3 mb-4" style={{ color: 'var(--brand-primary)' }}>Healing Focused</h4>
              <p className="body-medium">
                Douglas is passionate about helping you unlock your body's natural healing abilities.
              </p>
            </div>
          </div>
        </div>
        </div>
      </section>
    </>
  );
};